import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';

import { SlotHoldService } from '../../service/slot-hold.service';
import { Subscription } from 'rxjs';
import { DurationOption, AggregatedSlotDto, CourseSessionDto } from '../../service/court-availability-service';
import { VenueTimezoneService } from '../../service/venue-timezone.service';
import { AuthModalComponent } from '../../auth/components/auth-modal/auth-modal.component';
import { AuthService } from '../../auth/components/auth.service';
import { User } from '../../auth/shared/UserInterface';
import { AuthMode } from '../../auth/shared/auth-form.component';

/**
 * Summary + CTA card for the Einfache Ansicht:
 * - line 1 (small, gray): "Sa., 18. Juli · Padel", plus a "Kurs" badge at the
 *   end when a course session is selected
 * - line 2 (bold, single line, ellipsis-truncated): "15:30 – 17:00 · Platz 2"
 *   (court mode) / "20:00 – 21:30 · POP - U18" (course mode) / "Keine Auswahl"
 * - right: price ("28,00 €" / "—")
 * - CTA (amber btn-warning in both modes): "Jetzt reservieren" (court) /
 *   "Jetzt anmelden" (course) / disabled gray "Keine Auswahl". Unauthenticated
 *   users always get the "Anmelden zum Reservieren" button + auth modal.
 */
@Component({
  selector: 'app-booking-reserve',
  standalone: true,
  imports: [AuthModalComponent],
  templateUrl: './booking-reserve.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-reserve.component.css']
})
export class BookingReserveComponent implements OnDestroy {
  @Input() selectedDate: Date | null = null;
  @Input() categoryName = '';
  @Input() mode: 'court' | 'course' | null = null;
  @Input() courtName: string | null = null;
  @Input() selectedDuration: DurationOption | null = null;
  @Input() selectedSlot: AggregatedSlotDto | null = null;
  @Input() selectedCourseSession: CourseSessionDto | null = null;

  @Output() reserveClicked = new EventEmitter<AggregatedSlotDto>();
  @Output() enrollClicked = new EventEmitter<CourseSessionDto>();

  countdownDisplay = '';

  /* for auth modal */
  showAuthModal = false;
  authMode = AuthMode.LOGIN;
  private subscriptions: Subscription = new Subscription();

  private readonly priceFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });

  constructor(
    private slotHoldService: SlotHoldService,
    private venueTimezone: VenueTimezoneService,
    public authService: AuthService
  ) {
    // Subscribe to the countdown timer
    this.subscriptions.add(
      this.slotHoldService.getFormattedTimeRemaining().subscribe(time => {
        this.countdownDisplay = time;
      })
    );
  }

  // Helper method to safely get the current user
  getCurrentUser(): User | null | undefined {
    return this.authService.currentUserSignal();
  }

  openAuthModal(): void {
    this.authMode = AuthMode.LOGIN;
    this.showAuthModal = true;
  }

  onAuthSuccess(user: User): void {
    // Authentication succeeded, close the auth modal
    this.showAuthModal = false;
  }

  onAuthModalClosed(): void {
    this.showAuthModal = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ---- Selection completeness ----

  get isCourtSelectionComplete(): boolean {
    return (
      this.mode === 'court' &&
      !!this.selectedSlot &&
      !!this.selectedDuration &&
      !!this.courtName
    );
  }

  get isCourseSelectionComplete(): boolean {
    return this.mode === 'course' && !!this.selectedCourseSession;
  }

  // ---- Summary ----

  /** "Sa., 18. Juli · Padel" — selectedDate is a local calendar date. */
  get summaryLine1(): string {
    const parts: string[] = [];
    if (this.selectedDate) {
      parts.push(
        this.selectedDate.toLocaleDateString('de-DE', {
          weekday: 'short',
          day: 'numeric',
          month: 'long',
        })
      );
    }
    if (this.categoryName) {
      parts.push(this.categoryName);
    }
    return parts.join(' · ');
  }

  /** "15:30 – 17:00 · Platz 2" / "20:00 – 21:30 · POP - U18" / "Keine Auswahl". */
  get summaryLine2(): string {
    if (this.isCourseSelectionComplete) {
      const session = this.selectedCourseSession!;
      const start = this.venueTimezone.formatTime(session.startTimeUtc);
      const end = this.venueTimezone.formatTime(session.endTimeUtc);
      return `${start} – ${end} · ${session.displayName}`;
    }
    if (this.isCourtSelectionComplete) {
      const start = this.venueTimezone.formatTime(this.selectedSlot!.startTimeUtc);
      const end = this.venueTimezone.formatTime(this.selectedDuration!.endTime);
      return `${start} – ${end} · ${this.courtName}`;
    }
    return 'Keine Auswahl';
  }

  /** "28,00 €" / "—". */
  get summaryPrice(): string {
    if (this.isCourseSelectionComplete) {
      const session = this.selectedCourseSession!;
      return this.priceFormatter.format(session.priceForUser ?? session.regularPrice ?? 0);
    }
    if (this.isCourtSelectionComplete) {
      return this.priceFormatter.format(this.selectedDuration!.price);
    }
    return '—';
  }

  // ---- CTAs ----

  onReserveClick(): void {
    if (this.selectedSlot) {
      this.reserveClicked.emit(this.selectedSlot);
    }
  }

  onEnrollClick(): void {
    if (this.selectedCourseSession) {
      this.enrollClicked.emit(this.selectedCourseSession);
    }
  }

  // Check if current slot is being held
  get isSlotHeld(): boolean {
    if (!this.selectedSlot) return false;

    const heldSlot = this.slotHoldService.getCurrentHeldSlot();
    if (!heldSlot) return false;

    // Match on start time — always available even before backend response
    if (heldSlot.startTime !== this.selectedSlot.startTimeUtc) return false;

    // If the held slot has a court assigned (after backend response),
    // verify it belongs to this slot's courts (via breakdown, which lists all courts regardless of state)
    if (heldSlot.court?.id) {
      const allCourtIds = this.selectedSlot.courts.breakdown.map(c => c.id);
      return allCourtIds.includes(heldSlot.court.id);
    }

    // Court not yet assigned (hold request in flight) — startTime match is sufficient
    return true;
  }
}
